/**
 * InstagramSetupGuide - Guia passo-a-passo para configurar Instagram Direct API
 * 
 * Este componente exibe instruções detalhadas para o usuário configurar
 * o Instagram Direct Messaging via Meta Graph API.
 * 
 * O fluxo requer configuração manual pelo cliente:
 * 1. Conta profissional do Instagram (Business ou Creator)
 * 2. Vincular Instagram a uma página do Facebook
 * 3. Criar App no Meta Developer Portal
 * 4. Configurar produto "Instagram" no App
 * 5. Gerar Token de Acesso
 * 6. Copiar IDs e tokens para o formulário
 * 7. Configurar Webhook no Meta Developer Portal
 * 
 * @release v1.0 - 2026-01-17
 */

import { useState } from 'react';
import { 
  Instagram, 
  ExternalLink, 
  Copy, 
  Check, 
  ChevronDown, 
  ChevronUp,
  AlertTriangle,
  Info,
  Key,
  Globe,
  Link2,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ============= TIPOS =============

interface InstagramSetupGuideProps {
  /** URL do webhook que o cliente deve configurar no Meta */
  webhookUrl?: string;
  /** Token de verificação do webhook (gerado automaticamente ou personalizado) */
  verifyToken?: string;
  /** Se o guia está expandido por padrão */
  defaultExpanded?: boolean;
  /** Callback quando o usuário copia algo */
  onCopy?: (field: string, value: string) => void;
}

// ============= CONSTANTES =============

import { getInstagramWebhookUrl } from '@/lib/supabaseHelpers';

// Eventos que devem ser assinados no webhook - Organizados por categoria
// Obrigatórios: Essenciais para funcionamento básico
// Recomendados: Melhoram experiência do usuário
// Opcionais: Funcionalidades extras
const WEBHOOK_EVENTS = {
  required: [
    { name: 'messages', description: 'Mensagens de texto, mídia e stories' },
    { name: 'messaging_postbacks', description: 'Cliques em botões e quick replies' },
  ],
  recommended: [
    { name: 'messaging_reactions', description: 'Reações com emoji nas mensagens' },
    { name: 'message_reactions', description: 'Reações (Graph API alternativo)' },
    { name: 'messaging_referral', description: 'Referrals de links e anúncios' },
    { name: 'messaging_seen', description: 'Confirmação de leitura' },
  ],
  optional: [
    { name: 'comments', description: 'Comentários em posts/reels' },
    { name: 'live_comments', description: 'Comentários em lives' },
    { name: 'message_edit', description: 'Mensagens editadas' },
    { name: 'messaging_handover', description: 'Handover Protocol (apps múltiplos)' },
    { name: 'messaging_optins', description: 'Opt-ins de usuários' },
  ]
};

// ============= COMPONENTE =============

export function InstagramSetupGuide({ 
  webhookUrl,
  verifyToken = '',
  defaultExpanded = false,
  onCopy
}: InstagramSetupGuideProps) {
  // Obtém URL do webhook dinamicamente se não for passada
  const finalWebhookUrl = webhookUrl || getInstagramWebhookUrl();
  // Estado para controlar expansão do guia
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  /**
   * Copia texto para a área de transferência
   */
  const handleCopy = async (field: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      toast.success('Copiado para a área de transferência!');
      onCopy?.(field, value);
      
      // Reset do ícone após 2 segundos
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Erro ao copiar');
    }
  };

  /**
   * Renderiza botão de copiar com feedback visual
   */
  const CopyButton = ({ field, value }: { field: string; value: string }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleCopy(field, value)}
      className="h-8 px-2"
    >
      {copiedField === field ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </Button>
  );

  return (
    <div className="border rounded-lg bg-card">
      {/* Cabeçalho do Guia (Sempre visível) */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
            <Instagram className="h-5 w-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-foreground">Guia de Configuração</h3>
            <p className="text-sm text-muted-foreground">
              Instruções para conectar o Instagram Direct
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        )}
      </button>

      {/* Conteúdo Expandível */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Alerta de Token Manual */}
          <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-amber-700 dark:text-amber-300">
              <strong>Importante:</strong> O Instagram Direct API requer configuração manual. 
              Primeiro crie a inbox para gerar o Token de Verificação, depois configure o App no Meta Developer Portal.
            </AlertDescription>
          </Alert>

          {/* Alerta se não há verify token */}
          {!verifyToken && (
            <Alert variant="default" className="border-blue-500/50 bg-blue-500/10">
              <Info className="h-4 w-4 text-blue-500" />
              <AlertDescription className="text-blue-700 dark:text-blue-300">
                <strong>Dica:</strong> Gere ou preencha o Token de Verificação abaixo para poder configurar o webhook no Meta.
              </AlertDescription>
            </Alert>
          )}

          {/* Seção: URL do Webhook */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">URL do Webhook (Callback URL)</span>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50 border">
              <code className="flex-1 text-xs break-all font-mono">
                {finalWebhookUrl}
              </code>
              <CopyButton field="webhookUrl" value={finalWebhookUrl} />
            </div>
            <p className="text-xs text-muted-foreground">
              Cole esta URL no campo "Callback URL" nas configurações de Webhook do seu App.
            </p>
          </div>

          {/* Seção: Token de Verificação */}
          {verifyToken && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">Token de Verificação</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50 border">
                <code className="flex-1 text-xs break-all font-mono">
                  {verifyToken}
                </code>
                <CopyButton field="verifyToken" value={verifyToken} />
              </div>
              <p className="text-xs text-muted-foreground">
                Cole este token no campo "Verify Token" nas configurações de Webhook.
              </p>
            </div>
          )}

          {/* Seção: Eventos para Assinar - Expandida com categorias */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Eventos para Assinar</span>
            </div>
            
            {/* Obrigatórios */}
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-red-600 dark:text-red-400">
                Obrigatórios:
              </span>
              <div className="flex flex-wrap gap-1.5">
                {WEBHOOK_EVENTS.required.map((event) => (
                  <Badge 
                    key={event.name} 
                    variant="destructive" 
                    className="font-mono text-xs"
                    title={event.description}
                  >
                    {event.name}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Recomendados */}
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                Recomendados:
              </span>
              <div className="flex flex-wrap gap-1.5">
                {WEBHOOK_EVENTS.recommended.map((event) => (
                  <Badge 
                    key={event.name} 
                    variant="secondary" 
                    className="font-mono text-xs"
                    title={event.description}
                  >
                    {event.name}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Opcionais */}
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Opcionais:
              </span>
              <div className="flex flex-wrap gap-1.5">
                {WEBHOOK_EVENTS.optional.map((event) => (
                  <Badge 
                    key={event.name} 
                    variant="outline" 
                    className="font-mono text-xs opacity-80"
                    title={event.description}
                  >
                    {event.name}
                  </Badge>
                ))}
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground">
              Marque estes eventos no Webhook do Instagram. Passe o mouse sobre cada badge para ver a descrição.
            </p>
          </div>

          {/* Passos de Configuração */}
          <div className="space-y-3 pt-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" />
              Passos de Configuração
            </h4>
            
            <ol className="space-y-3 text-sm">
              <StepItem 
                number={1} 
                title="Conta Profissional"
                description="Certifique-se de ter uma conta Instagram Business ou Creator"
              />
              <StepItem 
                number={2} 
                title="Vincular ao Facebook"
                description="Conecte sua conta do Instagram a uma página do Facebook"
              />
              <StepItem 
                number={3} 
                title="Criar App Meta"
                description="Acesse developers.facebook.com e crie um App do tipo Business"
                link="https://developers.facebook.com/apps/"
              />
              <StepItem 
                number={4} 
                title="Adicionar Instagram"
                description="No App, adicione o produto 'Instagram' e configure 'Instagram API com login empresarial'"
              />
              <StepItem 
                number={5} 
                title="Gerar Token"
                description="Gere um token de acesso com permissões de mensagens do Instagram"
              />
              <StepItem 
                number={6} 
                title="Copiar IDs"
                description="Copie o Instagram Business Account ID e o Page ID vinculado"
              />
              <StepItem 
                number={7} 
                title="Configurar Webhook"
                description="Configure o Webhook com a URL e token acima"
              />
            </ol>
          </div>

          {/* Aviso sobre Token de 60 dias */}
          <Alert variant="default" className="border-blue-500/50 bg-blue-500/10">
            <Shield className="h-4 w-4 text-blue-500" />
            <AlertDescription className="text-blue-700 dark:text-blue-300">
              <strong>Atenção:</strong> Tokens de longa duração expiram após 60 dias. 
              Você precisará renová-los manualmente antes da expiração.
            </AlertDescription>
          </Alert>

          {/* Botão para Documentação */}
          <div className="flex justify-center pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('https://developers.facebook.com/docs/instagram-api/overview', '_blank')}
              className="gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Ver Documentação Oficial
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============= COMPONENTE AUXILIAR =============

interface StepItemProps {
  number: number;
  title: string;
  description: string;
  link?: string;
}

/**
 * Componente para renderizar um passo da configuração
 */
function StepItem({ number, title, description, link }: StepItemProps) {
  return (
    <li className="flex gap-3">
      <span className={cn(
        "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
        "bg-primary/10 text-primary border border-primary/20"
      )}>
        {number}
      </span>
      <div className="flex-1">
        <span className="font-medium text-foreground">{title}</span>
        <p className="text-muted-foreground text-xs mt-0.5">
          {description}
          {link && (
            <a 
              href={link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="ml-1 text-primary hover:underline inline-flex items-center gap-1"
            >
              Acessar
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </p>
      </div>
    </li>
  );
}

export default InstagramSetupGuide;
